// What went wrong with an answer, in words an engineer using the KB can act
// on: a title, what to do next, which button to offer, and the technical
// detail (behind "Details") when it adds something.
//
//   action: 'retry'  — Try again
//           'signin' — Sign in again
//           'new'    — Start a new question
//           null     — nothing the user can do here; the text says who can

import { ApiError } from './api.js'

const describe = (title, message, action = 'retry', details = null) => ({ title, message, action, details })

function technical(error) {
  const code = error?.code ?? error?.status
  const message = String(error?.message ?? error ?? '').trim()
  if (!message) return null
  return code && !message.includes(String(code)) ? `${code}: ${message}` : message
}

/** Errors our own API returned before the answer started. */
function fromApi(error) {
  const { status, code, message } = error
  if (status === 0) {
    return describe("Can't reach the Knowledge Base", 'Check your internet connection, then try again.')
  }
  if (status === 401) return describe('Your session has ended', 'Sign in again to keep asking questions.', 'signin')
  if (status === 403) {
    return describe("You can't add to this conversation", 'It belongs to someone else. Ask your question in a new conversation.', 'new')
  }
  if (status === 404) {
    return describe('This conversation no longer exists', 'It may have been deleted. Ask your question in a new conversation.', 'new')
  }
  if (status === 429) {
    return describe("You're asking questions too quickly", 'Wait a minute, then try again.')
  }
  if (code === 'too_long') return describe('Your question is too long', `${message} Shorten it and send it again.`, null)
  if (['no_skill', 'unknown_system', 'too_many_mentions'].includes(code)) {
    return describe('Check the systems you tagged', message, null)
  }
  if (status === 503) {
    return describe(
      "The AI service isn't set up",
      'Answers are switched off until a Knowledge Base admin finishes the AI provider settings.',
      null,
      message,
    )
  }
  if (status >= 500) {
    return describe(
      'Something went wrong on our side',
      'Try again. If it keeps happening, tell a Knowledge Base admin.',
      'retry',
      technical(error),
    )
  }
  return describe("Your question couldn't be sent", message || 'Try sending it again.')
}

// Failures reported by the AI provider while the answer streams. Matched on
// the provider's code first, then its message.
const PROVIDER = [
  {
    test: /overloaded|\b529\b|capacity/i,
    result: () =>
      describe('The AI service is busy', 'It has more requests than it can handle right now. Wait a moment, then try again.'),
  },
  {
    test: /prompt is too long|context.(length|window)|maximum context|too many tokens|request_too_large|\b413\b/i,
    result: () =>
      describe(
        'This conversation is too long',
        'The skills plus everything said so far no longer fit in one request. Start a new question to keep going.',
        'new',
      ),
  },
  {
    test: /credit balance|billing|insufficient_quota|quota exceeded/i,
    result: () =>
      describe(
        'The AI service account needs attention',
        'The AI provider account has run out of credit. Ask a Knowledge Base admin to check it.',
        null,
      ),
  },
  {
    test: /x-api-key|api.key|authentication_error|invalid_api_key|permission_error|\b401\b/i,
    result: () =>
      describe(
        'The AI service rejected our key',
        'The AI provider did not accept the configured API key. Ask a Knowledge Base admin to check it in Settings.',
        null,
      ),
  },
  {
    test: /rate.?limit|\b429\b/i,
    result: () =>
      describe('The AI service is rate-limited', 'Too many answers are being generated at once. Wait a minute, then try again.'),
  },
  {
    test: /timed? ?out|timeout|deadline|\b504\b/i,
    result: () => describe('The answer took too long', 'The AI service stopped responding. Try again; a narrower question is faster.'),
  },
  {
    test: /StreamTruncated|StreamRead|network|failed to fetch|load failed|terminated|connection|ECONNRESET|socket/i,
    result: () =>
      describe('The connection dropped', 'The answer stopped because the connection was interrupted. Check your connection, then try again.'),
  },
]

/** True when the user pressed Stop rather than something failing. */
export function isStopped(error) {
  const text = `${error?.name ?? ''} ${error?.code ?? ''} ${error?.message ?? error ?? ''}`
  return /AbortError|\baborted\b|Request aborted/i.test(text)
}

/** Describe an Error, an ApiError or a stored error string for the answer view. */
export function describeError(error) {
  if (!error) return null
  if (error instanceof ApiError) return fromApi(error)
  const text = `${error?.name ?? ''} ${error?.code ?? ''} ${error?.message ?? error}`
  for (const { test, result } of PROVIDER) {
    if (test.test(text)) return { ...result(), details: technical(error) }
  }
  return describe(
    "The answer couldn't be finished",
    'Something interrupted it. Try again; if it happens again, rephrase the question or start a new one.',
    'retry',
    technical(error),
  )
}
